
<template>
	<widget title="Payment Gateways" class="row-padding">
		<freemium-locked-alert v-if="$store.company.is_freemium == 1"></freemium-locked-alert>
		<vue-form v-on:submit="savePaymentSettings">

			<!------------------------------ Stripe Settings ---------------------------------->

			<div>
				<div class="d-flex justify-content-between align-items-center flex-wrap">
					<img :src="$store.apiRoot+'/resources/stripe-logo.svg.png'" class="integration-logo">

					<button v-if="payload.accept_stripe == 0" type="button" class="btn btn-primary" v-on:click="toggleStripe(1)">
						<i class="fa fa-toggle-on"></i>
						Activate
					</button>

					<template v-else>
						<strong class="text-success">Active</strong>

						<button class="btn btn-light" type="button" v-on:click="toggleStripe(0)">
							<i class="fa fa-toggle-off"></i>
							De-Activate
						</button>

					</template>

					<p class="text-muted">Stripe is a payment gateway which you can connect to your bank account and accept credit cards online.</p>
				</div>
				
				<div v-if="payload.accept_stripe == 1">
					<field label="Stripe Public Key"
						col-class="col-sm-6 col-md-4 col-lg-5"
						v-model="payload.stripe_public_key"
						:params="{type:'text',required:true}"
						:invalid="$store.errorList.stripe_public_key">
					</field>
					<field label="Stripe Secret Key"
						col-class="col-sm-6 col-md-4 col-lg-5"
						v-model="payload.stripe_secret_key"
						:params="{type:'text',required:true}"
						:invalid="$store.errorList.stripe_secret_key">
					</field>

					<field label="Send Stripe Receipts Upon Payment"
						col-class="col-sm-6 col-md-4 col-lg-5"
						v-model="payload.stripe_send_receipts"
						:params="{type:'boolean'}">

						<div class="text-muted">This will send a Stripe generated receipt via email to the customer</div>

						<img v-if="payload.stripe_send_receipts == 1" style="max-width: 300px" src="https://stripe.com/img/docs/dashboard/example_receipt.png">

					</field>
				</div>

			</div>

			<hr>


			<!------------------------------ PayPal Settings ---------------------------------->

			<div>
				<div class="d-flex justify-content-between align-items-center flex-wrap">
					<img :src="$store.apiRoot+'/resources/paypal-logo.png'" class="integration-logo">

					<button v-if="payload.accept_paypal == 0" type="button" class="btn btn-primary" v-on:click="togglePaypal(1)">
						<i class="fa fa-toggle-on"></i>
						Activate
					</button>

					<template v-else>
						<strong class="text-success">Active</strong>

						<button class="btn btn-light" type="button" v-on:click="togglePaypal(0)">
							<i class="fa fa-toggle-off"></i>
							De-Activate
						</button>

					</template>

					<p class="text-muted">Paypal is a payment gateway which can accept credit cards & Paypal transfers.</p>
				</div>

				<div v-if="payload.accept_paypal == 1">
					<field label="Paypal Email"
						col-class="col-sm-6 col-md-4 col-lg-5"
						v-model="payload.paypal_email"
						:params="{type:'text',required:true}"
						:invalid="$store.errorList.paypal_email">
					</field>
				</div>
			</div>

			<hr>


			<!------------------------------ Square Settings ---------------------------------->

			<div>
				<div class="d-flex justify-content-between align-items-center flex-wrap">
					<img :src="$store.apiRoot+'/resources/square-logo.png'" class="integration-logo">

					<button v-if="payload.accept_square == 0" type="button" class="btn btn-primary" v-on:click="toggleSquare(1)">
						<i class="fa fa-toggle-on"></i>
						Activate
					</button>

					<template v-else>
						<strong class="text-success">Active</strong>

						<button class="btn btn-light" type="button" v-on:click="toggleSquare(0)">
							<i class="fa fa-toggle-off"></i>
							De-Activate
						</button>

					</template>

					<p class="text-muted">Square is a payment gateway which can accept credit cards & Apple Pay.</p>
				</div>

				<div v-if="payload.accept_square == 1">
					<field label="Square Access Token"
							 col-class="col-sm-6 col-md-4 col-lg-5"
							 v-model="payload.square_access_token"
							 :params="{type:'text',required:true}"
							 :invalid="$store.errorList.square_access_token">
					</field>

					<field label="Square Business Location ID"
							 col-class="col-sm-6 col-md-4 col-lg-5"
							 v-model="payload.square_location_id"
							 :params="{type:'text',required:true}"
							 :invalid="$store.errorList.square_location_id">
					</field>
				</div>
			</div>
			<hr>

			<!-------------------- Custome Payment Gateway Link --------------->

			<div>
				<div class="d-flex justify-content-between align-items-center flex-wrap">

					<h3 class="d-flex align-items-center">
						<i class="fa fa-credit-card fa-3x mr-4"></i>
						Custom Payment Gateway Link
					</h3>

					<button v-if="payload.accept_custom_gateway == 0" type="button" class="btn btn-primary" v-on:click="toggleCustomGateway(1)">
						<i class="fa fa-toggle-on"></i>
						Activate
					</button>

					<template v-else>
						<strong class="text-success">Active</strong>

						<button class="btn btn-light" type="button" v-on:click="toggleCustomGateway(0)">
							<i class="fa fa-toggle-off"></i>
							De-Activate
						</button>

					</template>

					<p class="text-muted">Set up a custom payment page link</p>
				</div>

				<div v-if="payload.accept_custom_gateway == 1">
					<field label="Custom Gateway Page"
							 col-class="col-sm-6 col-md-4 col-lg-5"
							 v-model="payload.custom_payment_gateway_link"
							 :params="{type:'text',required:true,placeholder: 'https://your-payment-gateway.com'}"
							 :invalid="$store.errorList.custom_payment_gateway_link">
							 <div class="text-muted">Make sure to include the full address with https://</div>
					</field>
				</div>

			</div>

			<!------------------------------ General Payment Settings ------------------------------------->

			<template v-if="payload.accept_paypal == 1 || payload.accept_stripe == 1 || payload.accept_square == 1 || payload.accept_custom_gateway == 1">
				<hr>
				<h3>Payment Preferences</h3>
				<field label="Maximum Payment Value"
					col-class="col-sm-6 col-md-4 col-lg-5"
					v-model="payload.max_credit_value"
					:params="{type:'number',min:0}">

					<div class="text-muted">Any invoice with a total above the maximum payment value will not be payable online. For no limit, set it to 0.</div>

				</field>

				<field label="Automatically mark as 'Paid'"
					col-class="col-sm-6 col-md-4 col-lg-5"
					v-model="payload.auto_mark_paid"
					:params="{type:'boolean'}">
				</field>

				<field label="Send payment email notices to"
					col-class="col-sm-6 col-md-4 col-lg-5"
					v-model="emailNoticeUsers"
					:params="{type:'select-multi',options:userOptions()}">
				</field>
			</template>

		</vue-form>
	</widget>
</template>
<script type="text/javascript">
import VueForm from "@/components/VueForm"
import Field from "@/components/Field"

import Widget from "@/components/Widget"
import FreemiumLockedAlert from "@/components/FreemiumLockedAlert"

export default {
	name: "PaymentAcceptanceSettings",
	components: {
		Field,
		Widget,
		VueForm,
		FreemiumLockedAlert
	},
	data() {
		return {
			payload: {
				stripe_secret_key: "",
				stripe_public_key: "",
				stripe_send_receipts: true,
				paypal_email: "",
				square_access_token: "",
				square_location_id: "",
				custom_payment_gateway_link: "",
				accept_stripe: 0,
				accept_paypal: 0,
				accept_square: 0,
				accept_custom_gateway: 0,
				auto_mark_paid: 0,
				max_credit_value: 0
			},
			paymentMethodToggleKeys: ["accept_square","accept_paypal","accept_stripe","accept_custom_gateway"],
			emailNoticeUsers: [],
		}
	},
	watch: {
		'$store.company'() {
			let vm = this

			if ("CompanySetting" in this.$store.company)
				this.setPayload()

			if (this.$store.users.length == 0) {
				this.fetchUsers().then(response => {
					vm.setEmailNoticeUsers()
				})
			}
		},
		'$store.users'() {
			
		}
	},
	methods: {
		toggleStripe(activate) {
			this.payload.accept_stripe = activate
			this.$forceUpdate()
		},
		togglePaypal(activate) {
			this.payload.accept_paypal = activate
			this.$forceUpdate()
		},
		toggleSquare(activate) {
			this.payload.accept_square = activate
			this.$forceUpdate()
		},
		toggleCustomGateway(activate) {
			this.payload.accept_custom_gateway = activate
			this.$forceUpdate()
		},
		setEmailNoticeUsers() {
			let vm = this

			try {
				vm.emailNoticeUsers = JSON.parse(this.$store.company.CompanySetting.paid_email_notices_json)
			}
			catch (ex) {
				vm.emailNoticeUsers = []
			}
		},
		setPayload() {

			let setting = this.$store.company.CompanySetting, vm = this

			let payloadKeys = [
				"stripe_secret_key",
				"stripe_public_key",
				"stripe_send_receipts",
				"custom_payment_gateway_link",
				"paypal_email",
				"square_access_token",
				"square_location_id",
				"max_credit_value",
				"auto_mark_paid"
			]

			this.payload = {}

			payloadKeys.forEach(key => {
				vm.payload[key] = key in setting ? setting[key] : ""
			})

			this.setEmailNoticeUsers()

			this.paymentMethodToggleKeys.forEach(key => {
				if (setting[key] == 0)
					vm.payload[key] = 0
				else
					vm.payload[key] = 1
			})

		},
		savePaymentSettings() {
			let vm = this

			this.paymentMethodToggleKeys.forEach(key => {
				if (vm.payload[key])
					vm.payload[key] = 1
				else
					vm.payload[key] = 0
			})

			this.payload.paid_email_notices_json = JSON.stringify(this.emailNoticeUsers)

			this.postData("CompanySetting/save",this.payload,true).then(response => {
				if (response.success) {
					vm.alertSaved("Payment settings saved!")
				}
			})
		}
	},
	created() {
		if ("CompanySetting" in this.$store.company) {
			this.setPayload()
		}
	}
}
</script>

<style type="text/css">
	.toggle-label {
		opacity: 0;
		font-size: 14pt;
		font-weight: bold;
		height: 0;
		transition: height 0.5s, opacity 0.5s;
	}

	.integration-logo {
		max-width: 250px;
	}
</style>